import { useCallback, useEffect, useRef, useState } from "react";

import { describeError } from "../api/errors";
import { getTickets, getTicketsCount } from "../api/endpoints";
import type { TicketsFilter } from "../api/endpoints";
import type { TicketListItem, TicketsCursor } from "../api/types";

const PAGE_SIZE = 30;
const POLL_MS = 20_000;

interface TicketsState {
  tickets: TicketListItem[];
  cursor: TicketsCursor | null;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  openCount: number;
}

export interface UseTickets extends TicketsState {
  filter: TicketsFilter;
  userId: number | undefined;
  setFilter: (filter: TicketsFilter) => void;
  setUserId: (userId: number | undefined) => void;
  loadMore: () => void;
  refresh: () => void;
}

export function useTickets(): UseTickets {
  const [filter, setFilter] = useState<TicketsFilter>("open");
  const [userId, setUserId] = useState<number | undefined>(undefined);
  const [state, setState] = useState<TicketsState>({
    tickets: [],
    cursor: null,
    loading: true,
    loadingMore: false,
    error: null,
    openCount: 0,
  });

  /**
   * Which set of filters the answer being awaited belongs to.
   *
   * Switching a filter while a page is in flight would otherwise let the old answer arrive
   * last and paint the list of the filter nobody is looking at any more.
   */
  const generation = useRef(0);

  const load = useCallback(
    async (cursor: TicketsCursor | null) => {
      const current = cursor === null ? ++generation.current : generation.current;

      setState((previous) => ({
        ...previous,
        loading: cursor === null,
        loadingMore: cursor !== null,
        error: null,
      }));

      try {
        const [page, count] = await Promise.all([
          getTickets(filter, cursor, PAGE_SIZE, userId),
          getTicketsCount("open"),
        ]);

        if (current !== generation.current) {
          return;
        }

        setState((previous) => ({
          tickets: cursor === null ? page.tickets : [...previous.tickets, ...page.tickets],
          cursor: page.cursor,
          loading: false,
          loadingMore: false,
          error: null,
          openCount: count.count,
        }));
      } catch (error) {
        if (current !== generation.current) {
          return;
        }
        setState((previous) => ({
          ...previous,
          loading: false,
          loadingMore: false,
          error: describeError(error),
        }));
      }
    },
    [filter, userId],
  );

  useEffect(() => {
    void load(null);
  }, [load]);

  const refresh = useCallback(() => {
    void load(null);
  }, [load]);

  const loadMore = useCallback(() => {
    if (state.cursor && !state.loadingMore) {
      void load(state.cursor);
    }
  }, [load, state.cursor, state.loadingMore]);

  return {
    ...state,
    filter,
    userId,
    setFilter,
    setUserId,
    loadMore,
    refresh,
  };
}

export { POLL_MS as TICKETS_POLL_MS };
